// src/pages/Community/Missions/MissionDetails.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PageWrapper from "../../../components/PageWrapper";
import "../css/MissionDetail.css";
import FeedbackModal from "../components/FeedbackModal";
import {
  LikeBar,
  Comments,
  QA,
  ReactionBar,
  Bookmark,
  Spoiler,
  PresenceBadge,
  Discussion,
  MissionCompleted,
} from "../components";
import { UserName } from "../../../context/profileUI";
import MissionForm from "../../Profile/MissionForm"; // adjust path if needed
import Modal from "../components/Modal";

// Improved YouTube embed helper: tolerates raw IDs, youtu.be, embed/ and watch?v= forms
function toYouTubeEmbed(u = "") {
  if (!u) return null;
  try {
    // If it's likely a raw ID (no spaces, short-ish), return embed URL
    const idCandidate = u.trim();
    if (!idCandidate.includes(" ") && /^[A-Za-z0-9_-]{6,}$/.test(idCandidate) && !/\:|\//.test(idCandidate)) {
      return `https://www.youtube.com/embed/${idCandidate}`;
    }

    // normalize short links
    let normalized = u.replace("youtu.be/", "youtube.com/watch?v=");
    // ensure protocol for URL parsing
    if (!/^https?:\/\//i.test(normalized)) normalized = `https://${normalized}`;
    const url = new URL(normalized);

    // common query param
    const v = url.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;

    // fallback: last path segment (handles /embed/ID or /v/ID)
    const segments = url.pathname.split("/").filter(Boolean);
    const last = segments.pop();
    if (last && /^[A-Za-z0-9_-]{6,}$/.test(last)) return `https://www.youtube.com/embed/${last}`;

    return null;
  } catch {
    return null;
  }
}

export default function MissionDetail({ user: userFromRoute }) {
  const { id: missionId } = useParams();
  const location = useLocation();

  // ----- state + refs + hooks (ALL hooks must be declared before any early return) -----
  const [authUser, setAuthUser] = useState(null);
  const user = userFromRoute ?? authUser ?? null; // source of truth
  const uid = user?.uid ?? null;

  const [editOpen, setEditOpen] = useState(false);
  const [m, setM] = useState(undefined); // undefined = loading, null = not found
  const [completion, setCompletion] = useState(null); // user's mission progress
  const [linkCopied, setLinkCopied] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [fbOpen, setFbOpen] = useState(false);

  const basePath = useMemo(() => ["missions", missionId], [missionId]);
  const bookmarkKey = useMemo(() => `missions:${missionId}`, [missionId]);
  const [featuredReaction, setFeaturedReaction] = useState(null);
  const [selectedReactionFromBar, setSelectedReactionFromBar] = useState(null);

  // Auth listener
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => setAuthUser(u || null));
    return () => unsub();
  }, []);

  // Firestore mission doc listener
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "missions", missionId), (snap) => {
      setM(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
    return () => unsub();
  }, [missionId]);

  // Firestore user completion listener
  useEffect(() => {
    if (!uid) {
      setCompletion(null);
      return;
    }
    const compRef = doc(db, "missions", missionId, "completions", uid);
    const unsub = onSnapshot(compRef, (snap) => {
      setCompletion(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, [missionId, uid]);

  // Share timeout cleanup
  const shareTimeoutRef = useRef(null);
  useEffect(() => {
    return () => {
      if (shareTimeoutRef.current) {
        clearTimeout(shareTimeoutRef.current);
        shareTimeoutRef.current = null;
      }
    };
  }, []);

  const onShare = useCallback(async () => {
    const url = window.location.origin + location.pathname;
    try {
      if (navigator.share) {
        await navigator.share({ title: m?.missionName ?? "Mission", url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      if (shareTimeoutRef.current) clearTimeout(shareTimeoutRef.current);
      shareTimeoutRef.current = setTimeout(() => {
        setLinkCopied(false);
        shareTimeoutRef.current = null;
      }, 1800);
    } catch {
      // best-effort; ignore errors
    }
  }, [m?.missionName, location.pathname]);

  // Lightbox helpers (non-hook functions)
  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });
  const nextLight = (e) => {
    e?.stopPropagation?.();
    setLightbox((s) => {
      const len = m?.gallery?.length ?? 0;
      return { open: true, index: (s.index + 1) % len };
    });
  };
  const prevLight = (e) => {
    e?.stopPropagation?.();
    setLightbox((s) => {
      const len = m?.gallery?.length ?? 0;
      return { open: true, index: (s.index - 1 + len) % len };
    });
  };

  // keyboard handling for lightbox (hook placed before returns)
  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prevLight(e);
      else if (e.key === "ArrowRight") nextLight(e);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.open, /* prevLight/nextLight are stable here (closures) */]);

  // derived YouTube embed URL (hook placed before returns)
  const yt = useMemo(() => (m?.videoUrl ? toYouTubeEmbed(m.videoUrl) : null), [m?.videoUrl]);

  // ------------------------------------------------------------------------------------

  const canEdit =
    !!uid &&
    !!m &&
    (m.createdBy === uid ||
      (Array.isArray(m.allowEditBy) && m.allowEditBy.includes(uid)) ||
      m.isAdminEditable === true);

  const visibilityLabel = useMemo(() => {
    if (!m) return null;
    if (typeof m.isPrivate === "boolean") return m.isPrivate ? "Private" : "Public";
    return null;
  }, [m]);

  // early returns (now safely after all hooks)
  if (m === undefined) {
    return (
      <PageWrapper>
        <div className="msn-wrap">
          <div className="msn-skel-card" aria-busy="true" aria-live="polite">
            <div className="msn-skel-row">
              <div className="msn-skel-chip" />
              <div className="msn-skel-chip" />
              <div className="msn-skel-chip" />
            </div>
            <div className="msn-skel-title" />
            <div className="msn-skel-sub" />
            <div className="msn-skel-hero" />
            <div className="msn-skel-section" />
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (m === null) {
    return (
      <PageWrapper>
        <div className="msn-wrap">
          <div className="msn-empty-card" role="alert">
            <div className="msn-empty-title">We can’t find that mission</div>
            <p className="msn-empty-sub">
              It may have been removed or the link is incorrect.
            </p>
            <Link to="/community/missions" className="msn-btn msn-btn--primary">
              ← Back to missions
            </Link>
          </div>
        </div>
      </PageWrapper>
    );
  }

  const hasObjectives =
    (m.objectives?.primary?.length ?? 0) > 0 ||
    (m.objectives?.optional?.length ?? 0) > 0;

  // --- derived progress numbers (for % bar) ---
  const primaryCount = m.objectives?.primary?.length ?? 0;
  const optionalCount = m.objectives?.optional?.length ?? 0;
  const totalObjectives = primaryCount + optionalCount;

  // Normalize checklist: supports boolean arrays or index arrays
  function normalizeChecklist(arr, total) {
    if (!Array.isArray(arr) || total <= 0)
      return { doneIdx: [], doneSet: new Set() };

    // boolean flags: [true,false,true,...]
    if (typeof arr[0] === "boolean") {
      const idx = arr
        .map((v, i) => (v ? i : null))
        .filter((i) => i !== null && i < total);
      return { doneIdx: idx, doneSet: new Set(idx) };
    }

    // index array: [0,2,3]
    const idx = arr
      .filter((n) => Number.isInteger(n))
      .filter((n) => n >= 0 && n < total);
    return { doneIdx: idx, doneSet: new Set(idx) };
  }

  const primaryChecklist = normalizeChecklist(
    completion?.checklist?.primary,
    primaryCount
  );
  const optionalChecklist = normalizeChecklist(
    completion?.checklist?.optional,
    optionalCount
  );

  const donePrimary = primaryChecklist.doneIdx.length;
  const doneOptional = optionalChecklist.doneIdx.length;
  const doneAll = donePrimary + doneOptional;

  const pct =
    totalObjectives > 0
      ? Math.round((doneAll / totalObjectives) * 100)
      : completion?.completed
      ? 100
      : 0;

  return (
    <PageWrapper>
      <div className="msn-wrap">
        {/* Action / breadcrumb row */}
        <div className="msn-detail-toprow">
          <Link
            to="/community/missions"
            className="msn-back"
            aria-label="Back to missions"
          >
            ← All missions
          </Link>

          <div className="msn-actions">
            {m.videoUrl && (
              <a
                className="msn-action"
                href={m.videoUrl}
                target="_blank"
                rel="noreferrer"
              >
                ▶ Open video
              </a>
            )}
            <button className="msn-action" onClick={onShare}>
              {linkCopied ? "✓ Link copied" : "⤴ Share"}
            </button>
            <button className="msn-action" onClick={() => setFbOpen(true)}>
              💬 Feedback
            </button>

            {/* ✏️ Edit button (visible to creator) */}
            {canEdit && (
              <button
                className="msn-action"
                onClick={() => setEditOpen(true)}
                aria-label="Edit this mission"
                title="Edit this mission"
              >
                ✎ Edit
              </button>
            )}
          </div>
        </div>

        {/* Header */}
        <header className="msn-detail-header">
          <div className="msn-chips">
            <span className="msn-badge">{m.category || "Mission"}</span>
            {m.missionNumber != null && (
              <span className="msn-chip"># {m.missionNumber}</span>
            )}
            {m.difficulty && <span className="msn-chip">🛡 {m.difficulty}</span>}
            {m.estDurationMins && (
              <span className="msn-chip">⏱ ~{m.estDurationMins}m</span>
            )}
          </div>

          <h1 className="msn-detail-title">
            {m.missionName}
            {m.createdBy === uid && (
              <>
                <span title="Your mission" aria-label="Your mission">
                  {" "}
                  👑
                </span>
                {visibilityLabel && (
                  <span
                    className={`msn-chip ${
                      visibilityLabel === "Public"
                        ? "msn-chip--public"
                        : "msn-chip--private"
                    }`}
                    title={`Visibility: ${visibilityLabel}`}
                    aria-label={`Visibility: ${visibilityLabel}`}
                    style={{ marginLeft: "0.5rem" }}
                  >
                    {visibilityLabel === "Public" ? "🌐 Public" : "🔒 Private"}
                  </span>
                )}
              </>
            )}
          </h1>

          {/* ✅ put the featured/selected reaction right here */}
          {(featuredReaction || selectedReactionFromBar) && (
            <div className="msn-featured-reaction" aria-hidden>
              {(() => {
                const id = selectedReactionFromBar || featuredReaction;
                const map = {
                  like: "👍 Like",
                  love: "❤️ Love",
                  fire: "🔥 Hot",
                  helpful: "💡 Helpful",
                  strategy: "🎯 Strategy",
                  insightful: "🧠 Insightful",
                };
                return map[id] || id;
              })()}
            </div>
          )}

          {/* creator */}
          {m.createdBy && (
            <div className="msn-meta">
              <span>
                👤 Created by{" "}
                <strong>
                  <UserName userId={m.createdBy} fallback="Player" />
                </strong>
              </span>
            </div>
          )}

          <div className="msn-meta">
            {m.location && <span>📍 {m.location}</span>}
            {Array.isArray(m.tags) && m.tags.length > 0 && (
              <span className="msn-tags">
                {m.tags.map((t) => (
                  <Link
                    key={t}
                    to={`/missions?tag=${encodeURIComponent(t)}`}
                    className="msn-tag"
                    aria-label={`Tag ${t}`}
                  >
                    #{t}
                  </Link>
                ))}
              </span>
            )}
          </div>

          {m.coverImage && (
            <figure className="msn-hero-wrap">
              <img
                className="msn-hero"
                src={m.coverImage}
                alt={`${m.missionName} cover`}
                loading="lazy"
              />
              <figcaption className="visually-hidden">
                {m.missionName}
              </figcaption>
            </figure>
          )}
        </header>

        {/* Grid: main + aside */}
        <div className="msn-grid">
          <main className="msn-main">
            {/* Objectives */}
            {hasObjectives && (
              <section className="msn-section">
                <h2 className="msn-h2">Objectives</h2>
                <div className="msn-objwrap">
                  {m.objectives?.primary?.length > 0 && (
                    <div className="msn-card subtle">
                      <h3 className="msn-h3">Primary</h3>
                      <ul className="msn-list msn-progress-list">
                        {m.objectives.primary.map((obj, i) => {
                          const done = primaryChecklist.doneSet.has(i);
                          return (
                            <li key={i} className={done ? "done" : "pending"}>
                              {obj}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {m.objectives?.optional?.length > 0 && (
                    <div className="msn-card subtle">
                      <h3 className="msn-h3">Optional</h3>
                      <ul className="msn-list msn-progress-list">
                        {m.objectives.optional.map((obj, i) => {
                          const done = optionalChecklist.doneSet.has(i);
                          return (
                            <li key={i} className={done ? "done" : "pending"}>
                              {obj}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Walkthrough */}
            {m.walkthrough?.trim?.() && (
              <section className="msn-section">
                <h2 className="msn-h2">Walkthrough</h2>
                <div className="msn-md msn-prose">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {m.walkthrough}
                  </ReactMarkdown>
                </div>
              </section>
            )}

            {/* Approaches */}
            {m.approaches?.trim?.() && (
              <section className="msn-section">
                <h2 className="msn-h2">Different Approaches</h2>
                <div className="msn-md msn-prose">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {m.approaches}
                  </ReactMarkdown>
                </div>
              </section>
            )}

            {/* Tips */}
            {m.tips?.trim?.() && (
              <section className="msn-section">
                <h2 className="msn-h2">Tips & Tricks</h2>
                <div className="msn-md msn-prose">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {m.tips}
                  </ReactMarkdown>
                </div>
              </section>
            )}

            {/* Story */}
            {m.story?.trim?.() && (
              <section className="msn-section">
                <h2 className="msn-h2">Story</h2>
                <div className="msn-md msn-prose">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {m.story}
                  </ReactMarkdown>
                </div>
              </section>
            )}

            {/* Gallery */}
            {Array.isArray(m.gallery) && m.gallery.length > 0 && (
              <section className="msn-section">
                <h2 className="msn-h2">Gallery</h2>
                <div className="msn-gallery">
                  {m.gallery.map((src, i) => (
                    <button
                      key={i}
                      className="msn-shotbtn"
                      onClick={() => openLightbox(i)}
                      aria-label={`Open screenshot ${i + 1}`}
                    >
                      <img
                        className="msn-shot"
                        src={src}
                        alt={`Screenshot ${i + 1}`}
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Video (embed if possible) */}
            {m.videoUrl && (
              <section className="msn-section">
                <h2 className="msn-h2">Video</h2>
                {yt ? (
                  <div className="msn-video">
                    <iframe
                      title="Mission video"
                      src={yt}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <a
                    className="msn-link"
                    href={m.videoUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    ▶ Open video
                  </a>
                )}
              </section>
            )}
          </main>

          {/* Sidebar */}
          <aside className="msn-aside" aria-label="Quick facts">
            <div className="msn-card">
              <div className="msn-card-title">Quick facts</div>
              <div className="msn-facts">
                {m.difficulty && (
                  <div>
                    <span>Difficulty</span>
                    <strong>{m.difficulty}</strong>
                  </div>
                )}
                {m.estDurationMins && (
                  <div>
                    <span>Est. time</span>
                    <strong>{m.estDurationMins} mins</strong>
                  </div>
                )}
                {m.location && (
                  <div>
                    <span>Location</span>
                    <strong>{m.location}</strong>
                  </div>
                )}
              </div>
            </div>

            {(m.rewards?.cash != null || m.rewards?.items) && (
              <div className="msn-card">
                <div className="msn-card-title">Rewards</div>
                <div className="msn-rewards">
                  {m.rewards?.cash != null && (
                    <div className="msn-rewardchip">
                      💵 {m.rewards.cash.toLocaleString?.() ?? m.rewards.cash}
                    </div>
                  )}
                  {m.rewards?.items && (
                    <div className="msn-rewardchip">🎒 {m.rewards.items}</div>
                  )}
                </div>
              </div>
            )}

            {/* Progress card */}
            {uid ? (
              <div className="msn-card">
                <div className="msn-card-title">Your Progress</div>

                {/* Head summary */}
                <div className="msn-progress-head">
                  <div className="msn-progress-count">
                    <strong>{doneAll}</strong>/<span>{totalObjectives}</span>{" "}
                    objectives
                  </div>
                  <div className="msn-progress-pills">
                    {primaryCount > 0 && (
                      <span className="msn-pill">
                        Primary {donePrimary}/{primaryCount}
                      </span>
                    )}
                    {optionalCount > 0 && (
                      <span className="msn-pill">
                        Optional {doneOptional}/{optionalCount}
                      </span>
                    )}
                  </div>
                </div>

                {/* Percent bar */}
                <div
                  className="msn-progressbar"
                  aria-label={`Progress ${pct}%`}
                >
                  <div
                    className="msn-progressbar-fill"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="msn-progressbar-meta">{pct}% complete</div>

                {/* Collapsible checklists (easier to skim) */}
                <div className="msn-progress">
                  {/* Primary objectives list */}
                  {m.objectives?.primary?.length > 0 && (
                    <details className="msn-accordion" open>
                      <summary>Primary objectives</summary>
                      <ul className="msn-list msn-progress-list">
                        {m.objectives.primary.map((obj, i) => {
                          const done = primaryChecklist.doneSet.has(i);
                          return (
                            <li key={i} className={done ? "done" : "pending"}>
                              {obj}
                            </li>
                          );
                        })}
                      </ul>
                    </details>
                  )}

                  {/* Optional objectives list */}
                  {m.objectives?.optional?.length > 0 && (
                    <details className="msn-accordion">
                      <summary>Optional objectives</summary>
                      <ul className="msn-list msn-progress-list">
                        {m.objectives.optional.map((obj, i) => {
                          const done = optionalChecklist.doneSet.has(i);
                          return (
                            <li key={i} className={done ? "done" : "pending"}>
                              {obj}
                            </li>
                          );
                        })}
                      </ul>
                    </details>
                  )}

                  {completion?.completed && (
                    <p className="msn-complete-msg">🎉 Mission completed!</p>
                  )}
                  {!completion && (
                    <p className="msn-progress-hint">
                      Start completing objectives to track your progress.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="msn-card">
                <div className="msn-card-title">Your Progress</div>
                <p className="msn-progress-hint">
                  Sign in to track your progress.
                </p>
              </div>
            )}
          </aside>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox.open && Array.isArray(m.gallery) && m.gallery.length > 0 && (
        <div
          className="msn-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
        >
          <button
            className="msn-lightbox-close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            ✕
          </button>
          <button
            className="msn-lightbox-nav left"
            onClick={prevLight}
            aria-label="Previous"
          >
            ‹
          </button>
          <img
            className="msn-lightbox-img"
            src={m.gallery[lightbox.index]}
            alt={`Screenshot ${lightbox.index + 1}`}
          />
          <button
            className="msn-lightbox-nav right"
            onClick={nextLight}
            aria-label="Next"
          >
            ›
          </button>
        </div>
      )}

      {/* Engagement row */}
      <div className="msn-engage">
        <PresenceBadge presenceKey="missions" entityId={missionId} />
        <LikeBar basePath={basePath} />
        <ReactionBar
          basePath={basePath}
          visibleReactions={[
            "love",
            "fire", // Hot
            "helpful",
            "strategy",
            "insightful",
          ]}
          highlight={featuredReaction}
          onSelect={(id) => {
            setSelectedReactionFromBar(id);
          }}
        />

        <Bookmark bookmarkKey={bookmarkKey} />
        <MissionCompleted basePath={basePath} showCount mission={m} />
      </div>

      {/* Discussion thread */}
      <Discussion basePath={basePath} authorId={m.createdBy} />
      <FeedbackModal
        open={fbOpen}
        onClose={() => setFbOpen(false)}
        mission={m}
        sourceComponent="MissionDetails"
        source={location.pathname}
      />

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Mission"
        size="lg"
        closeOnBackdrop={true}
        showClose={true}
      >
        {m && (
          <MissionForm
            user={user}
            isAdmin={true}
            missionId={missionId}
            initial={m}
            onSaved={(id) => {
              console.log("Mission updated:", id);
              setEditOpen(false);
            }}
            onCancel={() => setEditOpen(false)}
            mode="edit"
          />
        )}
      </Modal>
    </PageWrapper>
  );
}
